import type { ItemPropTypes } from '@george-labs.com/design-system/types/components/Dropdown';

import { t } from '@luna/i18n';
import { getCurrentUrlWithoutHash } from '@luna/router';

import type { Contact } from '../ContactTypes';
import { getContactByIdFromContacts, getPrimaryContact } from '../ContactService';

export interface ContactItemPropTypes extends ItemPropTypes {
    isInvalid?: boolean;
}

export function isSelectedContactInvalid(contacts: Contact[], selectedContactId?: string): boolean {
    const selectedContact = contacts.find((contact) => contact.id === selectedContactId);
    return selectedContact?.isInvalid ?? false;
}

export function getSelectedContacts<T extends Contact>(
    contacts: T[],
    item: ItemPropTypes | ItemPropTypes[] | null,
): T[] {
    let items: ItemPropTypes[] = [];
    if (Array.isArray(item)) {
        items = item;
    } else if (item) {
        items.push(item);
    }
    return items
        .map((selectedItem) => getContactByIdFromContacts<T>(selectedItem.value, contacts))
        .filter((contact) => contact) as T[];
}

const isContactDefined = ({ isUndefined }: Contact): boolean => !isUndefined;

export function hasSomeDefinedContact(contacts: Contact[], isLoading: boolean): boolean {
    if (isLoading) {
        return true;
    }

    return contacts.some(isContactDefined);
}

export function getPlaceHolderText(
    contacts: Contact[],
    isLoading: boolean,
    hasError: boolean,
): string | undefined {
    if (hasError || hasSomeDefinedContact(contacts, isLoading)) {
        return undefined;
    }

    return t('contactDataManagement.contactSelect.nothingToSelectPlaceholder');
}

function getPreselectedId(contacts: Contact[], preselectPrimaryContact?: boolean): any {
    if (!preselectPrimaryContact) {
        return undefined;
    }

    return getPrimaryContact(contacts)?.id;
}

export function getSelectedContactId(
    selectedId: any,
    contacts: Contact[],
    isLoading: boolean,
    preselectPrimaryContact?: boolean,
): any {
    if (isLoading) {
        return undefined;
    }

    return selectedId ?? getPreselectedId(contacts, preselectPrimaryContact);
}

export function addBackUrlQueryParamToUrl(
    url: string,
    backUrl = getCurrentUrlWithoutHash(),
): string {
    return `${url}?back=${encodeURIComponent(backUrl)}`;
}

export const filterDefinedContacts = <T extends Contact>(contacts: T[]): T[] =>
    contacts.filter(isContactDefined);

export const hasSomeUndefinedContact = (contacts: Contact[]): boolean =>
    contacts.some(({ isUndefined }) => isUndefined);
